import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ChevronRight, Home as HomeIcon } from "lucide-react";
import { SITE_DOMAIN } from "./SEO";

/**
 * <Breadcrumb /> — visual breadcrumb + Schema.org BreadcrumbList JSON-LD.
 *
 * Props:
 *  - items: ordered array of { name, to }
 *           First entry should be "Home" (use to:"/"). Last entry is the current page;
 *           it renders unlinked.
 *
 * Visual is intentionally light (small, low-contrast separators) so it doesn't
 * compete with the page H1. JSON-LD mirrors the exact same node order.
 */
export const Breadcrumb = ({ items = [] }) => {
    if (!Array.isArray(items) || items.length < 2) return null;

    const schema = {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: items.map((item, idx) => ({
            "@type": "ListItem",
            position: idx + 1,
            name: item.name,
            item: item.to.startsWith("http") ? item.to : `${SITE_DOMAIN}${item.to}`,
        })),
    };

    return (
        <>
            <Helmet>
                <script type="application/ld+json">{JSON.stringify(schema)}</script>
            </Helmet>
            <nav
                aria-label="Breadcrumb"
                className="mb-5"
                data-testid="breadcrumb"
            >
                <ol className="flex items-center flex-wrap gap-1.5 text-[12px] text-text-dim">
                    {items.map((item, idx) => {
                        const isLast = idx === items.length - 1;
                        const isHome = idx === 0;
                        return (
                            <li
                                key={item.to + idx}
                                className="inline-flex items-center gap-1.5"
                            >
                                {idx > 0 && (
                                    <ChevronRight
                                        className="w-3 h-3 text-text-dim/70 shrink-0"
                                        aria-hidden="true"
                                    />
                                )}
                                {isLast ? (
                                    <span
                                        className="text-text-muted font-medium truncate max-w-[60vw]"
                                        aria-current="page"
                                    >
                                        {item.name}
                                    </span>
                                ) : (
                                    <Link
                                        to={item.to}
                                        className="inline-flex items-center gap-1 text-text-dim hover:text-accent-cyan transition-colors"
                                    >
                                        {isHome && (
                                            <HomeIcon
                                                className="w-3 h-3"
                                                aria-hidden="true"
                                            />
                                        )}
                                        {item.name}
                                    </Link>
                                )}
                            </li>
                        );
                    })}
                </ol>
            </nav>
        </>
    );
};

export default Breadcrumb;
